import moment from 'moment';
import React, { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';

import { DeviceOS } from '@vestahealthcare/common/enums';
import { translate } from '@vestahealthcare/common/i18n';
import {
  BaseEnum,
  BiometricsConfig,
  EmployeeGroup,
  Patient,
  ProgramExtension,
} from '@vestahealthcare/common/models';
import { NO_TIME_DATE_FORMAT_DO } from '@vestahealthcare/common/utils/constants';

import { Card, CardContent, CardHeader, IconButton } from 'styleguide-v2';

import {
  Device,
  MemberContactInfoParams,
  MemberContactInfoParsedResponse,
  PatientParam,
  ProgramExtensionBulkParams,
} from 'dash/src/services/PatientServices';

import { DetailColumn, DetailItem } from '../utils';
import { MemberContactInfoDetails } from './MemberContactInfoDetails';
import { MemberProgramEditModal } from './MemberProgramEditModal';
import { MemberProgramExtensionDetails } from './MemberProgramExtensionDetails';
import { MemberRPMVendorDetails } from './MemberRPMVendorDetails';
import { MemberSelfReporterDetails } from './MemberSelfReporterDetails';
import { MemberVirtualVisitDetail } from './MemberVirtualVisitDetail';

type Props = {
  configs?: BiometricsConfig[];
  contactInfo?: MemberContactInfoParsedResponse;
  data: {
    programExtensions: ProgramExtension[];
    selfReporterStatuses: BaseEnum[];
    worklistGroups: EmployeeGroup[];
  };
  member: Patient;
  ipad?: Device;
  onSendAppInvite: (member: Patient, deviceOs: DeviceOS) => Promise<void>;
  onSubmit: (
    params: Partial<
      { member: PatientParam } & { contactInfo: MemberContactInfoParams } & {
        programExtensions: ProgramExtensionBulkParams[];
      }
    >,
  ) => Promise<boolean>;
};

export const MemberProgramDetails = ({
  configs,
  contactInfo,
  data,
  member,
  ipad,
  onSendAppInvite,
  onSubmit,
}: Props) => {
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  return (
    <>
      <Card>
        <CardHeader
          title={
            <div className="flex gap">
              <span>
                {translate('personalDetails.programDetailsCard.title')}
              </span>
              <br />
              <IconButton
                disabled={!member?.isEditable()}
                noMargin
                onClick={() => setOpenEditModal(true)}
                size="small"
              >
                <EditIcon />
              </IconButton>
            </div>
          }
        />
        <CardContent>
          <div className="grid-wrapper fit">
            <DetailColumn>
              <DetailItem namespace="programDetailsCard" label="startDate">
                {(member?.reportingStartDate ?? undefined) &&
                  moment(member.reportingStartDate).format(
                    NO_TIME_DATE_FORMAT_DO,
                  )}
              </DetailItem>
              <DetailItem namespace="programDetailsCard" label="status">
                {member.status?.toString()}
              </DetailItem>
              <MemberVirtualVisitDetail member={member} />
              <DetailItem namespace="programDetailsCard" label="brand">
                {member.brand?.toString()}
              </DetailItem>
              <DetailItem namespace="programDetailsCard" label="ipadId">
                {ipad?.setupCode}
              </DetailItem>
              <DetailItem
                namespace="programDetailsCard"
                label="appVersion"
                noMarginBottom
              >
                {member.latestAppVersion}
              </DetailItem>
            </DetailColumn>

            <DetailColumn>
              <DetailItem
                namespace="programDetailsCard"
                label="preferredContactInfo"
              >
                <MemberContactInfoDetails contactInfo={contactInfo} />
              </DetailItem>
              <DetailItem namespace="programDetailsCard" label="selfReporter">
                <MemberSelfReporterDetails
                  member={member}
                  onSendAppInvite={onSendAppInvite}
                />
              </DetailItem>
              <DetailItem
                namespace="programDetailsCard"
                noMarginBottom
                label="programExtensions"
              >
                <MemberProgramExtensionDetails member={member} />
              </DetailItem>
            </DetailColumn>
            <DetailColumn>
              <DetailItem namespace="programDetailsCard" label="worklistGroup">
                {member?.worklistGroup?.name}
              </DetailItem>
              <DetailItem namespace="programDetailsCard" label="riskLevel">
                {member?.riskLevel?.description}
              </DetailItem>
              <p className="grid-span-12" />
              <p className="grid-span-12" />
              <MemberRPMVendorDetails configs={configs} member={member} />
            </DetailColumn>
          </div>
        </CardContent>
      </Card>
      <MemberProgramEditModal
        contactInfo={contactInfo}
        data={data}
        member={member}
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        onSubmit={onSubmit}
      />
    </>
  );
};
